import React, { MouseEvent, useCallback } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { MatchStatusList, MatchStatusType } from '../../../typings/match';
import {
  getMatchStatusLabel,
  isAbandoned,
  isCancelled,
  isEnded,
  isEndedAfterOverTime,
  isEndedAfterShootout,
  isLive,
  isPostponed,
} from '../../../utils/match-status';
import { getUrlStatic } from '../../../utils/placeholders-football';
import { makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { CompetitionBoardCardPreview, ImgPlaceholder, Text } from '../../atoms';
import Label from '../../atoms/label/label';
import './competition-board-card.scss';

const placeholder = `https://${getUrlStatic()}/images/skysport/configurations/placeholders/football/team-logo.png`;

interface ICompetitionBoardCardClasses {
  boardCard: string;
  header: string;
  title: string;
  matchInfo: string;
  matchStatus: string;
  matchInfoDot: string;
  content: string;
  teamContainer: string;
  teamContainerBorder: string;
  teamRow: string;
  prevMatch: string;
  teamRowWinner: string;
  teamName: string;
  teamInfo: string;
  teamImageContainer: string;
  score: string;
}

export type Winner = 'home' | 'away';

export const winnerList: Winner[] = ['home', 'away'];

export const statusList: MatchStatusType[] = MatchStatusList;

export interface ITeam {
  id?: string | null;
  name?: string | null;
  image?: string | null;
  score?: number;
  prevMatch?: { home: ITeam; away: ITeam; winner?: Winner };
}

export interface ICompetitionBoardCard extends IComponent {
  title: string;
  home: ITeam;
  away: ITeam;
  hidePrevMatches?: boolean;
  winner?: Winner;
  status: MatchStatusType;
  needConnector?: boolean;
  lmpUrl?: string;
  onPreviewClick?: () => void;
}

const classNames = (
  needConnector: ICompetitionBoardCard['needConnector'],
  lmpUrl?: ICompetitionBoardCard['lmpUrl']
): ICompetitionBoardCardClasses =>
  makeClassNames({
    boardCard: ['competition-board-card', lmpUrl && 'competition-board-card--show-animation'],
    header: 'competition-board-card__header',
    title: 'competition-board-card__title',
    content: ['competition-board-card__content', needConnector && 'competition-board-card__content--connector'],
    teamContainer: 'competition-board-card__team-container',
    teamContainerBorder: ['competition-board-card__team-container', 'competition-board-card__team-container--border'],
    teamRow: 'competition-board-card__team-row',
    prevMatch: 'competition-board-card__prev-match',
    teamRowWinner: ['competition-board-card__team-row', 'competition-board-card__team-row--winner'],
    teamName: 'competition-board-card__team-name',
    teamInfo: 'competition-board-card__team-info',
    teamImageContainer: 'competition-board-card__team-image-container',
    score: 'competition-board-card__score',
  }) as ICompetitionBoardCardClasses;

const getStatusLabel = (status: MatchStatusType): string => {
  if (isLive(status)) {
    return 'LIVE';
  }

  if (isEndedAfterOverTime(status)) {
    return 'DTS';
  }

  if (isEndedAfterShootout(status)) {
    return 'DCR';
  }

  return getMatchStatusLabel(status) || '';
};

const CompetitionBoardCard = ({
  home,
  away,
  winner,
  status,
  title,
  needConnector,
  lmpUrl,
  trackingData,
  hidePrevMatches = false,
  onPreviewClick,
}: ICompetitionBoardCard) => {
  const cn = classNames(needConnector, lmpUrl);

  const handlePreviewClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onPreviewClick?.();
    },
    [onPreviewClick]
  );

  return (
    <a
      href={lmpUrl || '#'}
      tabIndex={0}
      className={cn.boardCard}
      {...(trackingData ? objectToDataAttributes(trackingData) : {})}
      onClick={(e) => {
        if (!lmpUrl) {
          e.preventDefault();
        }
      }}
    >
      <div className={cn.header}>
        <Text className={cn.title} tag="p">
          {isEnded(status)
            ? t('match-end')
            : `${dateFormat({ date: title, format: 'ddd D MMM' })} | ore ${dateFormat({
                date: title,
                format: 'HH:mm',
              })}`}
        </Text>
        {(isLive(status) ||
          isEndedAfterOverTime(status) ||
          isEndedAfterShootout(status) ||
          isAbandoned(status) ||
          isPostponed(status) ||
          isCancelled(status)) && (
          <Label
            label={getStatusLabel(status) || ''}
            isCardLabel
            isBlackText={!isLive(status) && !isAbandoned(status) && !isPostponed(status) && !isCancelled(status)}
            isLive={isLive(status)}
          />
        )}
      </div>
      <div className={cn.content}>
        <div className={winner === 'home' ? cn.teamRowWinner : cn.teamRow}>
          {!hidePrevMatches && (
            <div className={cn.prevMatch}>
              <CompetitionBoardCardPreview
                onClick={handlePreviewClick}
                home={home.prevMatch?.home}
                away={home.prevMatch?.away}
                winner={home.prevMatch?.winner}
              />
            </div>
          )}
          <div className={cn.teamContainerBorder}>
            <div className={cn.teamInfo}>
              <div className={cn.teamImageContainer}>
                <ImgPlaceholder title={home?.name || ''} src={home?.image || undefined} placeholder={placeholder} />
              </div>
              <Text className={cn.teamName} tag="p">
                {home?.name?.toUpperCase() || t('competition.board.noname')}
              </Text>
            </div>
            {isEnded(status) && <div className={cn.score}>{home?.score}</div>}
          </div>
        </div>
        <div className={winner === 'away' ? cn.teamRowWinner : cn.teamRow}>
          {!hidePrevMatches && (
            <div className={cn.prevMatch}>
              <CompetitionBoardCardPreview
                onClick={handlePreviewClick}
                home={away.prevMatch?.home}
                away={away.prevMatch?.away}
                winner={away.prevMatch?.winner}
              />
            </div>
          )}
          <div className={cn.teamContainer}>
            <div className={cn.teamInfo}>
              <div className={cn.teamImageContainer}>
                <ImgPlaceholder title={away?.name || ''} src={away?.image || undefined} placeholder={placeholder} />
              </div>
              <Text className={cn.teamName} tag="p">
                {away?.name?.toUpperCase() || t('competition.board.noname')}
              </Text>
            </div>
            {isEnded(status) && <div className={cn.score}>{away?.score}</div>}
          </div>
        </div>
      </div>
    </a>
  );
};

export default CompetitionBoardCard;
